/**
 * The **server-side date-range read** the calendar does (`specs/M4-V4-calendar.md` §5,
 * decision **D-V4-1**).
 *
 * Round 5u placed every item the board had: one `BoardDataSource.load()` with no date
 * predicate, paged to exhaustion under a 2,000-item ceiling, then bucketed by day in the
 * browser. On a 4,020-item board (`[doc08]`, the biggest in the account) that is 4,020 rows
 * and their cells read to draw 31 days, and above the ceiling the grid says *"Showing the
 * first 2,000 items"* and is simply incomplete.
 *
 * The fix needs no new endpoint. `QueryState.filter` already travels with every read, the
 * service compiles it to SQL over the **date shadow** (`packages/shared/src/columns/sql.ts`,
 * `between` → `date_value BETWEEN ? AND ?`) and the in-memory source evaluates the same
 * operator, so the visible grid's window is expressible as an ordinary filter:
 *
 *     (date1 between [gridStart, gridEnd+1]) or (date2 between …)
 *
 * OR-ed because an item shows if **any** of the view's date columns places it there.
 *
 * **A `timeline` is the same read, with two ends.** It overlaps the window when
 * `from <= gridEnd and to >= gridStart`, which is one AND-ed pair of rules carrying monday's
 * `compare_attribute` (`"start"` / `"end"`). Round 5x could not send it — the attribute was
 * carried by the rule shape and then ignored by `filter-compiler.ts` *and* by the in-memory
 * `matchesRule`, so both halves landed on the same shadow and a range that started before the
 * window was dropped — so a view with a timeline column paged to the 2,000-item ceiling
 * instead. Round 5z made both evaluators honour the attribute
 * (`ColumnTypeDefinition.compareAttributes`), so the ceiling is lifted here too.
 *
 * `calendarEvents()` still clips to the exact grid, so the bound below may be generous — the
 * upper one deliberately is, by a day, because the shadow of a dated cell with a time sits
 * after that day's midnight.
 */
import { addDays } from '@elizmonday/shared';
import type { BoardColumn } from '../../board-view/data/types';
import type { FilterGroup, FilterRule } from '../../board-view/table/settings';

/**
 * The filter that narrows a read to the visible days, or `null` when the view cannot be
 * narrowed (no placing column, or one of them is neither a `date` nor a `timeline`) and the
 * caller must fall back to paging.
 */
export function dateRangeFilter(input: {
  readonly columns: readonly BoardColumn[];
  readonly dateColumnIds: readonly string[];
  /** Inclusive `YYYY-MM-DD` bounds of the rendered grid. */
  readonly rangeStart: string;
  readonly rangeEnd: string;
}): FilterGroup | null {
  const columns = input.dateColumnIds
    .map((id) => input.columns.find((column) => column.id === id))
    .filter((column): column is BoardColumn => column !== undefined);
  if (columns.length === 0) return null;
  if (columns.some((column) => column.type !== 'date' && column.type !== 'timeline')) return null;

  // One day of slack at the top: a `date` with a `time` shadows after that day's midnight.
  const upper = addDays(input.rangeEnd, 1);
  const rules: FilterRule[] = [];
  const groups: FilterGroup[] = [];
  for (const column of columns) {
    if (column.type === 'timeline') {
      // `from <= gridEnd and to >= gridStart` — the overlap, one rule per end.
      groups.push({
        operator: 'and',
        rules: [
          {
            columnId: column.id,
            operator: 'lower_than_or_equal',
            compareValue: upper,
            compareAttribute: 'start',
          },
          {
            columnId: column.id,
            operator: 'greater_than_or_equals',
            compareValue: input.rangeStart,
            compareAttribute: 'end',
          },
        ],
        groups: [],
      });
      continue;
    }
    rules.push({ columnId: column.id, operator: 'between', compareValue: [input.rangeStart, upper] });
  }
  return { operator: 'or', rules, groups };
}
